import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createElementBlock as _createElementBlock, Fragment as _Fragment, toDisplayString as _toDisplayString, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = {
  class: "mt-4",
  "data-cy": "conflict-log"
}
const _hoisted_2 = { class: "flex flex-wrap items-end gap-4" }
const _hoisted_3 = { class: "w-44" }
const _hoisted_4 = { class: "w-44" }
const _hoisted_5 = {
  key: 1,
  class: "mt-4 rounded border border-l-4 border-[rgba(var(--v-theme-error),0.3)] bg-[rgba(var(--v-theme-error),0.08)] px-3.5 py-3 text-xs leading-relaxed",
  "data-cy": "conflict-log-error"
}
const _hoisted_6 = { class: "mt-2.5" }
const _hoisted_7 = {
  key: 2,
  class: "mt-4 rounded border border-gray-200 bg-white px-3.5 py-3 text-xs leading-relaxed",
  "data-cy": "conflict-log-empty"
}
const _hoisted_8 = { class: "mt-2.5" }
const _hoisted_9 = ["href", "onClick"]
const _hoisted_10 = { class: "[overflow-wrap:anywhere]" }
const _hoisted_11 = { key: 0 }
const _hoisted_12 = {
  key: 1,
  class: "whitespace-nowrap"
}

import { onMounted, reactive } from 'vue';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import { parseLocalDate } from '@/services/utils';
import LlDataTable from '@/components/LlDataTable.vue';
import LlDataTablePagination from '@/components/LlDataTablePagination.vue';
import LlDatePicker from '@/components/LlDatePicker.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import { useConflictCheckRecordStore } from '../stores/useConflictCheckRecordStore';
import { formatRunAt, toApiDate } from '../utils/conflictDateHelpers';


export default /*@__PURE__*/_defineComponent({
  __name: 'ConflictCheckLog',
  props: {
    checkHref: { type: Function }
  },
  emits: ["open", "new"],
  setup(__props: any, { emit: __emit }) {



const emit = __emit;

/** The API caps pageSize at 100. */
const PAGE_SIZE_OPTIONS = [25, 50, 100];

const headers = [
  { title: 'Run', key: 'runAtUtc', sortable: false },
  { title: 'Run by', key: 'runByName', sortable: false },
  { title: 'Names checked', key: 'parties', sortable: false },
  { title: 'Results', key: 'counts', sortable: false },
  { title: 'Notes', key: 'hasNotes', sortable: false },
  { title: 'Time entries', key: 'includedTimeEntries', sortable: false },
];

const recordStore = useConflictCheckRecordStore();
const { handleError } = useSnackbarHandler();

const filterState = reactive<{ from: Date | null; to: Date | null }>({
  from: parseLocalDate(recordStore.filter.from),
  to: parseLocalDate(recordStore.filter.to),
});

async function load(): Promise<void> {
  try {
    await recordStore.loadLog();
  } catch (error) {
    handleError(error);
  }
}

function applyDates(): void {
  const from = toApiDate(filterState.from);
  const to = toApiDate(filterState.to);
  if (from === recordStore.filter.from && to === recordStore.filter.to) return;
  recordStore.setDateRange(from, to);
  load();
}

function clearDates(): void {
  filterState.from = null;
  filterState.to = null;
  applyDates();
}

function changePage(page: number): void {
  recordStore.setPage(page);
  load();
}

function changePageSize(pageSize: number): void {
  recordStore.setPageSize(pageSize);
  load();
}

onMounted(load);

return (_ctx: any,_cache: any) => {
  const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(LlDatePicker, {
          modelValue: filterState.from,
          "onUpdate:modelValue": [
            _cache[0] || (_cache[0] = ($event: any) => ((filterState.from) = $event)),
            applyDates
          ],
          label: "Run from",
          max: filterState.to || undefined,
          "data-cy": "conflict-log-from"
        }, null, 8, ["modelValue", "max"])
      ]),
      _createElementVNode("div", _hoisted_4, [
        _createVNode(LlDatePicker, {
          modelValue: filterState.to,
          "onUpdate:modelValue": [
            _cache[1] || (_cache[1] = ($event: any) => ((filterState.to) = $event)),
            applyDates
          ],
          label: "Run to",
          min: filterState.from || undefined,
          "data-cy": "conflict-log-to"
        }, null, 8, ["modelValue", "min"])
      ]),
      (_unref(recordStore).isFiltered)
        ? (_openBlock(), _createBlock(LlSecondaryButton, {
            key: 0,
            size: "small",
            class: "mb-1",
            "data-cy": "conflict-log-clear-dates",
            onClick: clearDates
          }, {
            default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
              _createTextVNode(" Clear dates ", -1)
            ]))]),
            _: 1
          }))
        : _createCommentVNode("", true)
    ]),
    (_unref(recordStore).logLoading && !_unref(recordStore).items.length)
      ? (_openBlock(), _createBlock(_component_v_skeleton_loader, {
          key: 0,
          class: "mt-4",
          type: "table-row-divider@5",
          "data-cy": "conflict-log-loading"
        }))
      : (_unref(recordStore).logError)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            _cache[5] || (_cache[5] = _createElementVNode("span", { class: "font-medium text-red-700" }, "Previous checks could not be loaded.", -1)),
            _cache[6] || (_cache[6] = _createTextVNode(" This is not an empty list — try again. ", -1)),
            _createElementVNode("div", _hoisted_6, [
              _createVNode(LlSecondaryButton, {
                size: "small",
                onClick: load
              }, {
                default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                  _createTextVNode("Try again", -1)
                ]))]),
                _: 1
              })
            ])
          ]))
        : (!_unref(recordStore).items.length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
              (_unref(recordStore).isFiltered)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _cache[7] || (_cache[7] = _createElementVNode("span", { class: "font-medium" }, "No checks were saved in these dates.", -1)),
                    _cache[8] || (_cache[8] = _createTextVNode(" Widen the dates or clear them to see every saved check. ", -1))
                  ], 64))
                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    _cache[10] || (_cache[10] = _createElementVNode("span", { class: "font-medium" }, "No saved checks yet.", -1)),
                    _cache[11] || (_cache[11] = _createTextVNode(" When anyone at the firm saves a check, it appears here with who ran it and when. ", -1)),
                    _createElementVNode("div", _hoisted_8, [
                      _createVNode(LlSecondaryButton, {
                        size: "small",
                        onClick: _cache[2] || (_cache[2] = ($event: any) => (emit('new')))
                      }, {
                        default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                          _createTextVNode(" Run a check ", -1)
                        ]))]),
                        _: 1
                      })
                    ])
                  ], 64))
            ]))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
              _createVNode(LlDataTable, {
                class: "mt-4 border border-gray-200",
                headers: headers,
                items: _unref(recordStore).items,
                "items-length": _unref(recordStore).total,
                loading: _unref(recordStore).logLoading,
                "must-sort": false,
                "data-cy": "conflict-log-table"
              }, {
                [`item.runAtUtc`]: _withCtx(({ item }) => [
                  _createElementVNode("a", {
                    href: __props.checkHref(item.id) ?? undefined,
                    class: "font-medium text-primary underline-offset-2 hover:underline",
                    "data-cy": "conflict-log-open",
                    onClick: _withModifiers(($event: any) => (emit('open', item.id)), ["prevent"])
                  }, _toDisplayString(_unref(formatRunAt)(item.runAtUtc)), 9, _hoisted_9)
                ]),
                [`item.parties`]: _withCtx(({ item }) => [
                  _createElementVNode("span", _hoisted_10, _toDisplayString(item.parties.join(' · ')), 1)
                ]),
                [`item.counts`]: _withCtx(({ item }) => [
                  (!item.fullCount && !item.partialCount)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_11, " No conflicts found "))
                    : (_openBlock(), _createElementBlock("span", _hoisted_12, _toDisplayString(item.fullCount) + " full · " + _toDisplayString(item.partialCount) + " partial ", 1))
                ]),
                [`item.hasNotes`]: _withCtx(({ item }) => [
                  _createTextVNode(_toDisplayString(item.hasNotes ? 'Yes' : 'None'), 1)
                ]),
                [`item.includedTimeEntries`]: _withCtx(({ item }) => [
                  _createTextVNode(_toDisplayString(item.includedTimeEntries ? 'Included' : 'Not searched'), 1)
                ]),
                _: 2
              }, 1032, ["items", "items-length", "loading"]),
              _createVNode(LlDataTablePagination, {
                page: _unref(recordStore).filter.page,
                "page-size": _unref(recordStore).filter.pageSize,
                "total-count": _unref(recordStore).total,
                "page-size-options": PAGE_SIZE_OPTIONS,
                "onUpdate:page": changePage,
                "onUpdate:pageSize": changePageSize
              }, null, 8, ["page", "page-size", "total-count"])
            ], 64))
  ]))
}
}

})